import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { ContentLayout5 } from '@components/content/components/content-layout-5/model';
import { HeroMenuService } from '@components/hero/components/hero-layout-1/services';
import { environment } from '@environments/environment';
import { GlobalService } from '@pages/global/services';
import { CommonService } from '@services/common';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { MiddleOfficeService } from '@services/middle-office';
import { PageTypeService } from '@services/page-type';
import { SeoService } from '@services/seo';
import { pageTypeBreadcrumbDisabled } from '@shared/components/breadcrumb/config';
import { BreadcrumbService } from '@shared/components/breadcrumb/services';
import { RoutesPath } from '@shared/constants';
import { CustomError, Layout, MenuAnchor } from '@shared/models';
import { Page, PageConfiguration } from '@shared/models/page';
import { toBasePageType } from '@shared/utils';
import { map, of, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { PageStore } from 'src/app/stores';

/**
 * Resolver function that attempts to fetch a SlugPage.
 * Returns an observable of SlugPage or undefined if an error occurs.
 * This resolver also handles an optional preview mode and replaces empty slugs with 'homepage'.
 */
export const slugPageResolver: ResolveFn<Page | undefined> = (route, state) => {
  const seoService = inject(SeoService);
  const commonService = inject(CommonService);
  const breadcrumbService = inject(BreadcrumbService);
  const contextService = inject(ContextService);
  const globalService = inject(GlobalService);
  const pageStore = inject(PageStore);
  const middleOfficeService = inject(MiddleOfficeService);
  const pageTypeService = inject(PageTypeService);
  const heroMenuService = inject(HeroMenuService);
  const router = inject(Router);
  const logsService = inject(LogsService);

  const setConfiguration = (config: PageConfiguration) => {
    commonService.setNav({ id: config.header.id, theme: config.header.theme });
    commonService.setFooter({ id: config.footer.id, theme: config.footer.theme });
    seoService.setSeo(config.seo);
    breadcrumbService.setBreadcrumb(config.breadcrumb);
    contextService.setContext({
      uuid: config.uuid,
      virtualUrl: config.virtualUrl,
    });
  };

  const buildMenuHero = (data: Page): void => {
    const menu: MenuAnchor[] = [];
    data.block.forEach((block, index) => {
      const i = index + 1;
      const layout = (block.data as Layout<never>).layout as string;
      if (layout === 'content_layout5') {
        const anchor = `block_${i}`;
        menu.push({
          label: ((block.data as Layout<ContentLayout5>).data as ContentLayout5).subtitle,
          anchor,
        });
      }
    });
    heroMenuService.setMenu(menu);
  };

  // Get the raw URL segment (remove query parameters if any)
  //    Example: "/about?preview=true" -> "/about"
  const urlWithoutQuery = state.url.split('?')[0] ?? '';

  // Remove any leading slash to isolate the slug
  //    Example: "/about" -> "about"
  const rawSlugPath = urlWithoutQuery.startsWith('/') ? urlWithoutQuery.slice(1) : urlWithoutQuery;

  // Extract the 'preview' query parameter
  //    If preview === 'true', we switch to preview mode
  const isPreview = route.queryParamMap.get('preview') === 'true';

  // If the slug is empty, replace with "homepage". Trim whitespaces if any.
  const slug = rawSlugPath.trim() === '' ? '/homepage' : `/${rawSlugPath.trim()}`;
  const canonicalLink = rawSlugPath.trim();
  seoService.setCanonicalLink(canonicalLink);
  logsService.trace('slugPageResolver', `Slug is "${slug}"`, isPreview ? 'on preview mode' : '');

  let request = globalService.getSlug(slug, isPreview ? { preview: 'true' } : {}, { cacheExpirationTime: 600 });

  if (environment.isPrefetchCmsEnabled) {
    request = globalService.readPrefetch(slug, { cacheExpirationTime: 600 });
  }

  return request.pipe(
    take(1),
    map((data) => {
      if (contextService.isBrowser()) {
        window.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
      if (
        pageTypeBreadcrumbDisabled.includes(data.type) ||
        (
          data?.block[0]!.data as {
            layout: string;
          }
        )?.layout === 'hero_layout2'
      ) {
        globalService.setWithBreadcrumb(false);
      } else {
        globalService.setWithBreadcrumb(true);
      }
      if (toBasePageType(data.type) === 'projects_details') buildMenuHero(data);
      return data;
    }),
    tap((data) => {
      pageStore.set(data);
      if (data.config.uuid) {
        middleOfficeService.currentStoreUuid$.next(data.config.uuid);
      } else {
        middleOfficeService.currentStoreUuid$.next(null);
      }
      pageTypeService.setPageType(data.type);
      setConfiguration(data.config);
    }),
    catchError((error: CustomError) => {
      logsService.error('slugPageResolver', 'Page resolution failed', { slug, isPreview }, error);
      if (error.status !== 0) {
        void router.navigate([`${RoutesPath.HOME}`]);
      }
      return of(undefined);
    })
  );
};
